/**
 * Da quale offerta partire per il rinnovo.
 *
 * Il rinnovo si fa su un'offerta precisa, e cercarla in elenco per premere la
 * sua icona è un passaggio che non viene in mente: da qui si parte dalla
 * funzione e si sceglie l'offerta, che è il modo in cui ci si pensa a gennaio
 * — "quali contratti vanno rinnovati?".
 *
 * Si propongono quelle degli anni passati, le vinte per prime: sono quelle
 * che un rinnovo ce l'hanno davvero.
 */
import { useMemo, useState } from 'react';
import Modal from './Modal';

const fmt = (n) => Number(n || 0).toLocaleString('it-IT', { style: 'currency', currency: 'EUR', maximumFractionDigits: 0 });
const annoDi = (r) => new Date(r.data_emissione || r.created_at || Date.now()).getFullYear();

export default function SceltaDaRinnovare({ offerte = [], onScelta, onClose }) {
  const [cerca, setCerca] = useState('');
  const annoCorrente = new Date().getFullYear();

  // Già rinnovata: da qualche parte in elenco c'è l'offerta che ne è nata.
  const rinnovate = useMemo(
    () => new Set(offerte.map(o => o.rinnovo_di).filter(Boolean).map(String)),
    [offerte]);

  const candidate = useMemo(() => {
    const parole = cerca.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const testo = (r) => [r.numero, r.titolo, r.cliente_nome, r.cliente_ragione_sociale]
      .filter(Boolean).join(' ').toLowerCase();
    return offerte
      .filter(r => r.stato !== 'persa' && r.stato !== 'bozza')
      .filter(r => !parole.length || parole.every(p => testo(r).includes(p)))
      .sort((a, b) => {
        // Prima le vinte non ancora rinnovate, poi per anno e numero: è
        // l'ordine in cui si guarda chi va rinnovato.
        const peso = (r) => (r.stato === 'vinta' ? 0 : 1) + (rinnovate.has(String(r.id)) ? 2 : 0);
        return peso(a) - peso(b)
          || annoDi(b) - annoDi(a)
          || String(a.numero).localeCompare(String(b.numero));
      })
      .slice(0, 60);
  }, [offerte, cerca, rinnovate]);

  return (
    <Modal title="Quale offerta vuoi rinnovare?" onClose={onClose} size="lg">
      <div className="space-y-3">
        <input className="input" autoFocus placeholder="Cerca per cliente, numero o oggetto…"
               value={cerca} onChange={e => setCerca(e.target.value)} />

        {candidate.length === 0 ? (
          <p className="text-sm text-gray-400 text-center py-6">
            Nessuna offerta da rinnovare con questa ricerca.
          </p>
        ) : (
          <div className="max-h-[22rem] overflow-y-auto border rounded-lg divide-y">
            {candidate.map(r => {
              const gia = rinnovate.has(String(r.id));
              return (
                <button key={r.id} type="button"
                        className="w-full text-left px-3 py-2 hover:bg-brand-50/70 flex items-center gap-3"
                        onClick={() => onScelta(r)}>
                  <div className="flex-1 min-w-0">
                    <div className="text-sm font-medium text-gray-800 truncate">
                      <span className="font-mono text-gray-500 mr-2">{r.numero}</span>
                      {r.cliente_nome || '—'}
                    </div>
                    <div className="text-xs text-gray-500 truncate">{r.titolo}</div>
                  </div>
                  <div className="text-right shrink-0">
                    <div className="text-sm font-medium">{fmt(r.importo)}</div>
                    <div className="text-xs text-gray-400">
                      {annoDi(r)}
                      {annoDi(r) === annoCorrente && ' · quest’anno'}
                    </div>
                  </div>
                  {/* Rinnovarla due volte è un errore che si scopre tardi */}
                  {gia && (
                    <span className="text-[11px] px-1.5 py-0.5 rounded bg-amber-100 text-amber-700 shrink-0">
                      già rinnovata
                    </span>
                  )}
                </button>
              );
            })}
          </div>
        )}
      </div>

      <div className="flex justify-end mt-4">
        <button className="btn-secondary" onClick={onClose}>Annulla</button>
      </div>
    </Modal>
  );
}
